import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import { ArrowUpward, ArrowDownward } from '@material-ui/icons';

import Select from '../UI/Select/Select';
import SortOption from '../../entities/sortOption';
import SortSelectorMultiOptionType from '../../types/sortSelectorMultiOption';
import SortSelectorOption from './SortSelectorOption';

import './SortSelector.scss';

const separatorOption = {
	id: 'separator',
	label: '',
	isSeparator: true,
	isDisabled: true
};

// Helper functions
const getDirectionIcon = ( direction, className ) => ( direction === 'ascending'
	? <ArrowUpward className={className} />
	: <ArrowDownward className={className} /> );

const TooltipContent = () => (
	<div className="sort-tooltip-content">
		<div className="tooltip-section">
			<div className="tooltip-header">
				<span className="tooltip-arrow">↑</span>
				<span className="tooltip-label">Ascending</span>
			</div>
			<div className="tooltip-description">A→Z, Old→New, Low→High</div>
		</div>
		<div className="tooltip-section">
			<div className="tooltip-header">
				<span className="tooltip-arrow">↓</span>
				<span className="tooltip-label">Descending</span>
			</div>
			<div className="tooltip-description">Z→A, New→Old, High→Low</div>
		</div>
	</div>
);

const SortSelector = ( {
	options, selected, onChange, ascending
} ) => {
	const [ isDropdownOpen, setIsDropdownOpen ] = useState( false );
	const [ hoveredDirection, setHoveredDirection ] = useState( null );

	// Check if we need a separator (only for projects view, not for specific project)
	// In projects view, the first option is an individual SortOption object
	// In specific project view, the first option is a group
	const isIndividualOption = option => option.id && option.name && !option.options;
	const needsSeparator = options.length > 0 && isIndividualOption( options[ 0 ] );

	const optionsWithDirection = useMemo( () => ( needsSeparator
		? [
			...SortOption.directionOptions,
			separatorOption,
			...options
		]
		: [
			...SortOption.directionOptions,
			...options
		] ), [ needsSeparator, options ] );

	const onOptionChange = useCallback( ( newSelected ) => {
		if ( newSelected.isDirection ) {
			// User selected a direction, keep current criteria but change direction
			if ( selected ) {
				onChange( selected, newSelected.ascending );
			}
		} else {
			// User selected a criteria, use current direction
			onChange( newSelected, ascending );
		}
	}, [ selected, onChange, ascending ] );

	const displayValueFormatter = useCallback( ( label ) => {
		if ( !selected ) return label;

		return (
			<span className="display-value-container">
				{getDirectionIcon( ascending ? 'ascending' : 'descending', 'display-value-icon' )}
				{label}
			</span>
		);
	}, [ selected, ascending ] );

	const createCustomOption = useCallback( props => (
		<SortSelectorOption
			{...props}
			ascending={ascending}
			selected={selected}
			onDirectionHover={setHoveredDirection}
		/>
	), [ ascending, selected ] );

	return (
		<div className="SortSelector">
			<Select
				selected={selected}
				placeholder="No Sort"
				options={optionsWithDirection}
				onChange={onOptionChange}
				selectedOptionType="Sort:"
				keepOpenOnSelect
				customOption={createCustomOption}
				displayValueFormatter={displayValueFormatter}
				onMenuOpen={() => setIsDropdownOpen( true )}
				onMenuClose={() => setIsDropdownOpen( false )}
			/>
			{isDropdownOpen && hoveredDirection && (
				<div className="sort-tooltip-wrapper">
					<TooltipContent />
				</div>
			)}
		</div>
	);
};

SortSelector.propTypes = {
	options: PropTypes.oneOfType( [
		PropTypes.arrayOf(
			SortSelectorMultiOptionType
		),
		PropTypes.arrayOf(
			PropTypes.instanceOf( SortOption )
		)
	] ),
	selected: PropTypes.instanceOf( SortOption ),
	onChange: PropTypes.func,
	ascending: PropTypes.bool
};

SortSelector.defaultProps = {
	options: [],
	selected: undefined,
	onChange: () => {},
	ascending: true
};

export default SortSelector;
